import { ReactNode } from "react";

import {
    Button,
    CARD_WIDTH,
    Column,
    MEDIA,
    MediaQuery,
    MediaStack,
    Row,
    SPACING,
    SxType,
    useAppPalette
} from "@bridgesplit/ui";
import { COPY } from "app/constants";

import { CARD_Z_INDEX, MOBILE_PADDING, NAV_BREAKPOINT, NAV_Z_INDEX, useNavbarHeight } from "../navbar";
import { HeaderWithClose } from "../common";

type CardWrapperProps = {
    breakpoint?: MediaQuery;
    contentSpacing?: number;
    pageSpacing?: number;
    cardSx?: SxType;
    children: ReactNode;
    cardDisplay?: "important" | "checkout";
};
export function SideCardWrapper({
    breakpoint = MEDIA.XL,
    contentSpacing = 4,
    pageSpacing = 4,
    sideCard,
    cardSx,
    children,
    reversed
}: CardWrapperProps & { sideCard: ReactNode; reversed?: boolean }) {
    const { stickyTop } = useNavbarHeight();
    return (
        <MediaStack
            query={breakpoint}
            spacing={pageSpacing}
            sxBelow={{ flexDirection: reversed ? "column-reverse" : "column" }}
            sxAbove={{ alignItems: "flex-start", flexDirection: reversed ? "row-reverse" : "row" }}
        >
            <Column
                spacing={contentSpacing}
                sx={{ width: "100%", minWidth: 0, [breakpoint.above]: { flex: "1 1 0%" } }}
            >
                {children}
            </Column>
            <Column
                spacing={2}
                sx={{
                    width: "100%",
                    [breakpoint.above]: {
                        maxWidth: CARD_WIDTH,
                        top: stickyTop,
                        position: "sticky",
                        height: "fit-content",
                        zIndex: CARD_Z_INDEX
                    },
                    ...cardSx
                }}
            >
                {sideCard}
            </Column>
        </MediaStack>
    );
}

export function MobileBottomCta({ children }: { children: ReactNode }) {
    const { navFooter, footer } = useNavbarHeight();
    const { background, border } = useAppPalette();

    return (
        <>
            <Row
                sx={{
                    zIndex: NAV_Z_INDEX - 1,
                    position: "fixed",
                    bottom: footer,
                    [NAV_BREAKPOINT.below]: { bottom: navFooter + footer },
                    left: 0,
                    width: "100vw",
                    background,
                    borderTop: border
                }}
            >
                <Column sx={{ flexGrow: 1 }}>
                    <Row sx={{ m: 2, flexGrow: 1 }} spacing={1}>
                        {children}
                    </Row>
                </Column>
            </Row>
            <Column id="virtual-cta" sx={{ height: "40px" }} />
        </>
    );
}

export function MobileCtaWrapper({
    open,
    setOpen,
    children,
    card,
    ctaText
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    children: ReactNode;
    card: ReactNode;
    ctaText?: string;
}) {
    const { height } = useNavbarHeight();
    const { border, background } = useAppPalette();
    const containerHeight = `calc(100dvh - ${height + SPACING * MOBILE_PADDING * 2}px)`;

    return (
        <Column sx={{ height: containerHeight, minHeight: 0, overflow: "hidden" }}>
            {open ? (
                <Column sx={{ flex: 1, overflowY: "auto", minHeight: 0 }} spacing={2}>
                    <HeaderWithClose variant="h4" close={() => setOpen(false)} header={ctaText} />
                    {card}
                </Column>
            ) : (
                <>
                    <Column sx={{ flex: 1, overflowY: "auto", minHeight: 0 }} spacing={2}>
                        {children}
                    </Column>
                    {card && (
                        <Row sx={{ borderTop: border, background, px: 2, py: 2 }} spacing={1}>
                            <Button fullWidth variant="contained" color="secondary" onClick={() => setOpen(true)}>
                                {ctaText ?? COPY.LOOP_TERM_ACTION}
                            </Button>
                        </Row>
                    )}{" "}
                </>
            )}
        </Column>
    );
}
